import { ItemAddonBehaviour, ItemAddonType } from '@order-mage/integration-sdk';
import { uuidv7 } from 'uuidv7';
import { MAX_ADDON_NESTING_DEPTH } from '@/api/menu-list/max-addon-nesting-depth';
import type { StorefrontAddonDef } from '@/api/menu-list/storefront-menu-snapshot.types';
import type { CartMenuItem, MenuListsData } from '@/entities/cart/lib/item-price';
import {
    buildPackageItemByIdMap,
    enrichPackageWithPriceableItem,
    isPackageApplicable,
    shouldIncludePackages,
} from '@/entities/cart/lib/packages';
import { getContainedSinglePickHash, getHashFromItemIdMap, getMemberHash } from '@/entities/cart/lib/pricing';
import { roundPrice } from '@/shared/lib/priceRound';
import { Country } from '@/shared/model/Country';
import { Currency } from '@/shared/model/Currency';
import { Language, LocalizedString } from '@/shared/model/Language';

type MenuEntryLike = {
    item?: CartMenuItem;
    body?: {
        id?: string;
        name?: LocalizedString;
        addons?: StorefrontAddonDef[];
        variants?: Array<{ id: string; itemId: string; isDefault?: boolean }>;
        packages?: CartMenuItem['packages'];
        packageItemIds?: string[];
        packageBehaviours?: CartMenuItem['packageBehaviours'];
        prices?: CartMenuItem['prices'];
    };
};

type ItemsMap = Record<string, MenuEntryLike | undefined>;

/** Cart MenuListsData uses `.item`; storefront index uses `.body`. */
const entryPayload = (entry: MenuEntryLike | undefined) => entry?.body ?? entry?.item;

const flattenAddons = (addons: StorefrontAddonDef[]): StorefrontAddonDef[] =>
    addons.flatMap((addon) => (addon.type === ItemAddonType.GROUP ? (addon.groupMembers ?? []) : [addon]));

/**
 * Returns the addon definitions that match the actually selected item. When a
 * variant has its own addons (different definition IDs and members from the parent),
 * those must be used — otherwise the selection (`cartAddons`) is looked up under
 * the parent's IDs and the variant's multi-pick addons are silently dropped from
 * both display and price calculation.
 *
 * Falling back to the parent addons mirrors `applyParentFallbacks`: a variant with
 * no addons of its own inherits the parent's.
 */
export const resolveEffectiveAddons = (
    itemsMap: ItemsMap,
    parentAddons: StorefrontAddonDef[] | null | undefined,
    variant: string | null | undefined,
    hash: string,
): StorefrontAddonDef[] | null | undefined => {
    if (variant && variant !== hash) {
        const variantAddons = entryPayload(itemsMap[variant])?.addons;
        if (variantAddons?.length) {
            return variantAddons;
        }
    }
    return parentAddons;
};

type AddonChanges = {
    added: { count: number; name: LocalizedString }[];
    removed: { name: LocalizedString }[];
};

/**
 * Process addon changes to determine what was added/removed compared to defaults
 */
export const processAddonChanges = (
    itemAddons: StorefrontAddonDef[],
    selectedAddons: Record<string, string | Record<string, number>>,
    menuItems: Record<string, MenuEntryLike>,
): AddonChanges => {
    const added: { count: number; name: LocalizedString }[] = [];
    const removed: { name: LocalizedString }[] = [];
    const idToHash = getHashFromItemIdMap(menuItems);

    for (const addon of flattenAddons(itemAddons)) {
        const cartValue = selectedAddons[addon.id];

        if (addon.behaviour === ItemAddonBehaviour.SINGLE_PICK) {
            const containedHash = getContainedSinglePickHash(addon.members, idToHash);
            const currentHash = typeof cartValue === 'string' ? cartValue : (containedHash ?? '');
            if (currentHash === containedHash) continue;
            if (currentHash) {
                const name = entryPayload(menuItems[currentHash])?.name;
                if (name) added.push({ count: 1, name });
            }
            if (containedHash) {
                const name = entryPayload(menuItems[containedHash])?.name;
                if (name) removed.push({ name });
            }
            continue;
        }

        // MULTI_PICK and MULTI_PICK_CONFIGURABLE both send the change against the contained amount.
        const cartMultiValue = (typeof cartValue === 'object' && cartValue) || {};
        for (const member of addon.members) {
            const memberHash = getMemberHash(member, idToHash);
            if (!memberHash) continue;

            const defaultQty = Number(member.amount?.value ?? 0);
            const currentQty = cartMultiValue[memberHash] ?? 0;

            if (currentQty > defaultQty) {
                const name = entryPayload(menuItems[memberHash])?.name;
                if (name) added.push({ count: currentQty - defaultQty, name });
            } else if (currentQty < defaultQty) {
                const name = entryPayload(menuItems[memberHash])?.name;
                if (name) removed.push({ name });
            }
        }
    }

    return { added, removed };
};

export type AddonChangeLine = {
    name: LocalizedString;
    itemId?: string;
    count: number;
    variantName?: LocalizedString;
    children: AddonChangeLine[];
};

const finiteQty = (value: number): number => (Number.isFinite(value) ? Math.max(0, value) : 0);

/**
 * Selected addons as a tree of changes from the contained amount, same as the
 * cart pills. A member with no change is left out unless it anchors a nested
 * change or a non-default variant. A full removal does not keep children. A
 * partial reduction shows the removed delta and keeps the remaining pieces as
 * that anchor. Below the root, a section the cart never wrote still means the
 * contained amount. A level past the drawing cap is left out, and a repeated
 * item on the same branch is left out.
 */
export const selectedAddonLines = (
    itemAddons: StorefrontAddonDef[] | null | undefined,
    selectedAddons: Record<string, string | Record<string, number>> | undefined,
    menuItems: ItemsMap,
    memberVariants?: Record<string, string>,
): AddonChangeLine[] => {
    if (!itemAddons?.length || !selectedAddons) return [];
    const idToHash = getHashFromItemIdMap(menuItems);

    const walk = (defs: StorefrontAddonDef[], depth: number, seen: Set<string>): AddonChangeLine[] => {
        if (depth >= MAX_ADDON_NESTING_DEPTH) return [];
        const lines: AddonChangeLine[] = [];

        const lineFor = (hash: string, count: number): AddonChangeLine | null => {
            const payload = entryPayload(menuItems[hash]);
            if (!payload?.id || !payload.name || seen.has(payload.id)) return null;
            const nextSeen = new Set(seen);
            nextSeen.add(payload.id);
            const rows = (payload.variants ?? []).flatMap((row) => ('id' in row && row.id ? [row] : []));
            const chosen = memberVariants?.[payload.id];
            const active = rows.find((row) => row.id === chosen) ?? rows.find((row) => row.isDefault);
            let variantName: LocalizedString | undefined;
            let nested = payload.addons;
            if (active && !active.isDefault) {
                const variantHash = idToHash.get(active.itemId);
                const variantPayload = variantHash ? entryPayload(menuItems[variantHash]) : undefined;
                if (variantPayload?.name) variantName = variantPayload.name;
                if (variantPayload?.addons?.length) nested = variantPayload.addons;
            }
            return {
                name: payload.name,
                itemId: payload.id,
                count,
                ...(variantName ? { variantName } : {}),
                children: nested?.length ? walk(nested, depth + 1, nextSeen) : [],
            };
        };

        const removalLine = (hash: string, count: number): AddonChangeLine | null => {
            const payload = entryPayload(menuItems[hash]);
            if (!payload?.name) return null;
            return {
                name: payload.name,
                ...(payload.id ? { itemId: payload.id } : {}),
                count,
                children: [],
            };
        };

        for (const addon of flattenAddons(defs)) {
            const cartValue = selectedAddons[addon.id];
            if (addon.behaviour === ItemAddonBehaviour.SINGLE_PICK) {
                const containedHash = getContainedSinglePickHash(addon.members, idToHash);
                const currentHash = typeof cartValue === 'string' ? cartValue : (containedHash ?? '');
                if (currentHash && currentHash !== containedHash) {
                    const line = lineFor(currentHash, 1);
                    if (line) lines.push(line);
                } else if (currentHash && currentHash === containedHash) {
                    const line = lineFor(currentHash, 0);
                    if (line && (line.children.length > 0 || line.variantName)) lines.push(line);
                }
                if (containedHash && containedHash !== currentHash) {
                    const removed = removalLine(containedHash, -1);
                    if (removed) lines.push(removed);
                }
                continue;
            }

            const sectionMissing = cartValue == null;
            const cartMultiValue = (typeof cartValue === 'object' && cartValue) || {};
            for (const member of addon.members) {
                const memberHash = getMemberHash(member, idToHash);
                if (!memberHash) continue;
                const contained = finiteQty(Number(member.amount?.value ?? 0));
                const written = !sectionMissing && memberHash in cartMultiValue;
                // A nested section the cart never wrote still means "keep what is included".
                const current =
                    depth > 0 && !written ? contained : finiteQty(Number(cartMultiValue[memberHash] ?? 0));
                if (current < contained) {
                    const removed = removalLine(memberHash, -(contained - current));
                    if (removed) lines.push(removed);
                    if (current <= 0) continue;
                }
                const line = lineFor(memberHash, Math.max(0, current - contained));
                if (line && (line.count > 0 || line.children.length > 0 || line.variantName)) {
                    lines.push(line);
                }
            }
        }

        return [...lines.filter((line) => line.count >= 0), ...lines.filter((line) => line.count < 0)];
    };

    return walk(itemAddons, 0, new Set());
};

type MenuItems = Record<string, MenuEntryLike>;

type CartAddons = Record<string, string | Record<string, number>>;

type OrderAddonPackage = {
    id: string;
    price?: string;
    item?: {
        name: LocalizedString;
        trait: string;
    };
};

/** Card scale for a synthesized line. Cash rounding stays on the order total. */
const linePrice = (amount: number): string => String(roundPrice(amount, Currency.CZK, Country.CZ, 'card'));

/** Kept pieces pay the variant package minus the member's package. */
const netKeptPackages = (
    variantPackages: OrderAddonPackage[] | undefined,
    defaultPackages: OrderAddonPackage[] | undefined,
): OrderAddonPackage[] | undefined => {
    const totals = new Map<string, OrderAddonPackage & { amount: number }>();
    for (const pkg of variantPackages ?? []) {
        totals.set(pkg.id, { ...pkg, amount: Number(pkg.price ?? 0) });
    }
    for (const pkg of defaultPackages ?? []) {
        const existing = totals.get(pkg.id);
        const amount = (existing?.amount ?? 0) - Number(pkg.price ?? 0);
        totals.set(pkg.id, { ...(existing ?? pkg), amount });
    }
    const list = [...totals.values()]
        .filter((pkg) => pkg.amount !== 0)
        .map(({ amount, ...pkg }) => ({ ...pkg, price: linePrice(amount) }));
    return list.length > 0 ? list : undefined;
};

type OrderAddonSelection = {
    id: string;
    parentId: string | null;
    addonId: string;
    itemId: string;
    quantity: string;
    price?: string;
    selectedVariantId?: string | null;
    packages?: OrderAddonPackage[];
};

type GetItemPricesFn = (
    item: string | Pick<CartMenuItem, 'id' | 'prices' | 'packages' | 'packageBehaviours'>,
) => { itemPrice: { amount: string; currency: string } } | null;

export const mapCartAddonsToOrderAddons = (
    cartAddons: CartAddons | undefined,
    itemAddonDefs: StorefrontAddonDef[] | null | undefined,
    menuItems: MenuItems,
    getItemPrices: GetItemPricesFn,
    menuListsData?: MenuListsData,
    deliveryMethodDefinitionId?: string,
    userLanguage?: Language,
    memberVariants?: Record<string, string>,
): OrderAddonSelection[] => {
    if (!itemAddonDefs || !cartAddons) {
        return [];
    }

    const idToHash = getHashFromItemIdMap(menuItems);
    const packageItemById = buildPackageItemByIdMap(menuListsData?.menuLists);

    const walk = (
        defs: StorefrontAddonDef[],
        parentId: string | null,
        depth: number,
        path: Set<string>,
    ): OrderAddonSelection[] => {
        if (depth >= MAX_ADDON_NESTING_DEPTH) return [];

        return flattenAddons(defs).flatMap((addon) => {
            const addonId = addon.id;
            const userValue = cartAddons[addonId];
            const hasContainedMember = addon.members.some((m) => Number(m.amount?.value ?? 0) > 0);
            if (!userValue && !hasContainedMember) return [];

            const addonItems: Array<{
                itemId: string;
                quantity: string;
                price?: string;
                packages?: OrderAddonPackage[];
            }> = [];

            const getAddonItemPackages = (memberEntry: MenuEntryLike): OrderAddonPackage[] | undefined => {
                const payload = entryPayload(memberEntry);
                // The storefront index carries `packageItemIds`, the cart map a
                // materialised `packages`; an addon member can arrive as either.
                const memberPackages =
                    payload?.packages ??
                    payload?.packageItemIds?.map((packageItemId) => ({
                        id: packageItemId,
                        packageItemId,
                        packageItem: packageItemById.get(packageItemId),
                    }));

                if (
                    !payload ||
                    !memberPackages?.length ||
                    !shouldIncludePackages(payload, deliveryMethodDefinitionId)
                ) {
                    return undefined;
                }

                const packages = memberPackages
                    .map((pkg): OrderAddonPackage | null => {
                        const enriched = enrichPackageWithPriceableItem(pkg, packageItemById);
                        if (
                            !enriched.packageItem ||
                            !isPackageApplicable(
                                enriched,
                                deliveryMethodDefinitionId,
                                userLanguage ?? Language.EN_GB,
                            )
                        ) {
                            return null;
                        }

                        const priceValue = getItemPrices(enriched.packageItem);
                        return {
                            id: pkg.id,
                            price: priceValue?.itemPrice.amount,
                            item: {
                                name: enriched.packageItem.name,
                                trait: enriched.packageItem.trait ?? '',
                            },
                        };
                    })
                    .filter((p): p is OrderAddonPackage => p !== null);

                return packages.length > 0 ? packages : undefined;
            };

            // A nested section the cart never wrote still means "keep what is included".
            // The root keeps today's rule: a missing key is a removal of the contained amount.
            const fillMissing = depth > 0;
            const selectedItemIds = new Set<string>();
            const selectedCountByItemId = new Map<string, number>();
            const containedCountByItemId = new Map<string, number>();

            if (addon.behaviour === ItemAddonBehaviour.SINGLE_PICK) {
                // Keeping the contained member sends nothing; a switch sends +new and a free −contained.
                const containedHash = getContainedSinglePickHash(addon.members, idToHash);
                const selectedHash = typeof userValue === 'string' ? userValue : (containedHash ?? '');
                const selectedPayload = selectedHash ? entryPayload(menuItems[selectedHash]) : undefined;
                const containedPayload = containedHash ? entryPayload(menuItems[containedHash]) : undefined;
                if (containedPayload?.id) containedCountByItemId.set(containedPayload.id, 1);
                if (selectedPayload?.id) {
                    selectedItemIds.add(selectedPayload.id);
                    selectedCountByItemId.set(selectedPayload.id, 1);
                }
                if (selectedHash !== (containedHash ?? '')) {
                    if (selectedHash && selectedPayload?.id) {
                        const memberItem = menuItems[selectedHash];
                        const itemPrice = getItemPrices(selectedPayload as CartMenuItem);
                        addonItems.push({
                            itemId: selectedPayload.id,
                            quantity: '1',
                            price: itemPrice?.itemPrice.amount,
                            packages: memberItem ? getAddonItemPackages(memberItem) : undefined,
                        });
                    }
                    if (containedPayload?.id) {
                        addonItems.push({ itemId: containedPayload.id, quantity: '-1', price: '0' });
                    }
                }
            } else {
                const userMembers = (typeof userValue === 'object' && userValue) || {};
                const containedAmounts: Record<string, number> = {};
                for (const member of addon.members) {
                    const memberHash = getMemberHash(member, idToHash);
                    if (memberHash) {
                        containedAmounts[memberHash] = Number(member.amount?.value ?? 0);
                    }
                }
                const resolved: Record<string, number> = fillMissing ? { ...containedAmounts } : {};
                for (const [memberHash, userQty] of Object.entries(userMembers)) {
                    resolved[memberHash] = Number(userQty);
                }
                if (!fillMissing) {
                    for (const memberHash of Object.keys(containedAmounts)) {
                        if (!(memberHash in userMembers)) resolved[memberHash] = 0;
                    }
                }

                for (const [memberHash, normalizedQty] of Object.entries(resolved)) {
                    const containedAmount = containedAmounts[memberHash] ?? 0;
                    const memberItem = menuItems[memberHash];
                    const payload = entryPayload(memberItem);
                    if (!payload?.id) continue;
                    if (containedAmount > 0) containedCountByItemId.set(payload.id, containedAmount);
                    if (normalizedQty > 0) {
                        selectedItemIds.add(payload.id);
                        selectedCountByItemId.set(payload.id, normalizedQty);
                    }
                    if (normalizedQty === containedAmount) continue;

                    const itemPrice = getItemPrices(payload as CartMenuItem);
                    if (normalizedQty > containedAmount) {
                        addonItems.push({
                            itemId: payload.id,
                            quantity: `${normalizedQty - containedAmount}`,
                            price: itemPrice?.itemPrice.amount,
                            packages: memberItem ? getAddonItemPackages(memberItem) : undefined,
                        });
                    } else {
                        addonItems.push({
                            itemId: payload.id,
                            quantity: `-${containedAmount - normalizedQty}`,
                            price: '0',
                        });
                    }
                }
            }

            const consolidatedItems = new Map<string, (typeof addonItems)[0]>();
            addonItems.forEach((item) => {
                const existing = consolidatedItems.get(item.itemId);
                if (existing) {
                    const existingQty = parseInt(existing.quantity, 10);
                    const itemQty = parseInt(item.quantity, 10);
                    const newQty = existingQty + itemQty;
                    if (newQty !== 0) {
                        existing.quantity = `${newQty}`;
                        if (itemQty > 0 && item.price) {
                            existing.price = item.price;
                            existing.packages = item.packages;
                        }
                    } else {
                        consolidatedItems.delete(item.itemId);
                    }
                } else {
                    consolidatedItems.set(item.itemId, { ...item });
                }
            });

            const finalItems = Array.from(consolidatedItems.values());
            const selections: OrderAddonSelection[] = [];
            const emitted = new Set<string>();

            const chosenVariant = (itemId: string) => {
                const chosen = memberVariants?.[itemId];
                if (!chosen) return undefined;
                const memberHash = idToHash.get(itemId);
                const variant = menuItems[memberHash ?? '']?.body?.variants?.find((row) => row.id === chosen);
                if (!variant?.id || variant.isDefault) return undefined;
                return variant;
            };

            const variantSurcharge = (itemId: string) => {
                const variant = chosenVariant(itemId);
                if (!variant) return undefined;
                const variantHash = idToHash.get(variant.itemId);
                const variantEntry = variantHash ? menuItems[variantHash] : undefined;
                const variantPayload = entryPayload(variantEntry);
                // The default variant is empty, its price and packages sit on the member itself.
                const memberHash = idToHash.get(itemId);
                const defaultEntry = memberHash ? menuItems[memberHash] : undefined;
                const defaultPayload = entryPayload(defaultEntry);
                const variantAmount = variantPayload
                    ? Number(getItemPrices(variantPayload as CartMenuItem)?.itemPrice.amount ?? 0)
                    : 0;
                const defaultAmount = defaultPayload
                    ? Number(getItemPrices(defaultPayload as CartMenuItem)?.itemPrice.amount ?? 0)
                    : 0;
                return {
                    selectedVariantId: variant.id,
                    unitDelta: variantAmount - defaultAmount,
                    applyPrice: Boolean(variantPayload),
                    packages: variantEntry ? getAddonItemPackages(variantEntry) : undefined,
                    defaultPackages: defaultEntry ? getAddonItemPackages(defaultEntry) : undefined,
                };
            };

            const keptPieces = (itemId: string) => {
                const selected = selectedCountByItemId.get(itemId) ?? 0;
                const contained = containedCountByItemId.get(itemId) ?? 0;
                return Math.min(selected, Math.max(0, contained));
            };

            const nestedSelections = (itemId: string, id: string): OrderAddonSelection[] => {
                if (!selectedItemIds.has(itemId)) return [];
                const memberHash = idToHash.get(itemId);
                const variant = chosenVariant(itemId);
                const variantHash = variant ? idToHash.get(variant.itemId) : undefined;
                const variantAddons = variantHash ? entryPayload(menuItems[variantHash])?.addons : undefined;
                const memberAddons = memberHash ? entryPayload(menuItems[memberHash])?.addons : undefined;
                const nested = variantAddons?.length ? variantAddons : memberAddons;
                if (!nested?.length) return [];
                const childPath = new Set(path);
                childPath.add(itemId);
                if (variant) childPath.add(variant.itemId);
                return walk(nested, id, depth + 1, childPath);
            };

            for (const line of finalItems) {
                if (path.has(line.itemId)) continue;
                const id = uuidv7();
                const lineQty = Number(line.quantity);
                const charge = lineQty > 0 ? variantSurcharge(line.itemId) : undefined;
                const rowId = charge?.selectedVariantId;
                if (charge?.applyPrice && lineQty > 0) {
                    if (charge.unitDelta !== 0) {
                        line.price = linePrice(Number(line.price ?? 0) + charge.unitDelta);
                    }
                    line.packages = charge.packages;
                }
                selections.push({
                    id,
                    parentId,
                    addonId,
                    itemId: line.itemId,
                    quantity: line.quantity,
                    ...(line.price !== undefined ? { price: line.price } : {}),
                    ...(rowId ? { selectedVariantId: rowId } : {}),
                    ...(line.packages ? { packages: line.packages } : {}),
                });
                // A negative line is only the removed pieces. The pieces that remain
                // still need their own row, so they are not marked done here.
                if (lineQty > 0 && keptPieces(line.itemId) <= 0) {
                    emitted.add(line.itemId);
                    selections.push(...nestedSelections(line.itemId, id));
                }
            }

            for (const member of addon.members) {
                const memberHash = getMemberHash(member, idToHash);
                if (
                    !member.itemId ||
                    !memberHash ||
                    emitted.has(member.itemId) ||
                    path.has(member.itemId) ||
                    !selectedItemIds.has(member.itemId)
                ) {
                    continue;
                }
                const id = uuidv7();
                const children = nestedSelections(member.itemId, id);
                const charge = variantSurcharge(member.itemId);
                if (!children.length && !charge?.selectedVariantId) continue;
                const pieces = keptPieces(member.itemId);
                const priced = Boolean(charge?.applyPrice && pieces > 0 && charge.unitDelta !== 0);
                const keptPackages = charge
                    ? netKeptPackages(charge.packages, charge.defaultPackages)
                    : undefined;
                selections.push(
                    {
                        id,
                        parentId,
                        addonId,
                        itemId: member.itemId,
                        quantity: charge && pieces > 0 ? String(pieces) : '0',
                        price: priced && charge ? linePrice(charge.unitDelta) : '0',
                        ...(charge ? { selectedVariantId: charge.selectedVariantId } : {}),
                        ...(keptPackages ? { packages: keptPackages } : {}),
                    },
                    ...children,
                );
            }

            return selections;
        });
    };

    return walk(itemAddonDefs, null, 0, new Set());
};
